/**
 * BS Category Config Module
 *
 * Static TypeScript config defining the Balance Sheet hierarchy.
 * Drives both Detailed and Simplified view rendering in the financial module.
 *
 * Structure:
 *   BS_STRUCTURE — full category/subcategory/item tree + calculated rows
 *   getCategoryForBSLineItem(id) — reverse lookup: item id → category key
 *   getSubcategoryForBSLineItem(id) — reverse lookup: item id → subcategory key
 *   getSimplifiedBSRows() — 8 calculated total row definitions for Simplified view
 *   getAllBSInputRows() — all editable line items across all categories
 *
 * Sprint: SPRINT-14
 * Feature: F003 — BS Category Config Module
 *
 * @module bs-category-config
 */

export type BSLineItemType =
  | 'cash_business_account'
  | 'accounts_receivable'
  | 'inventory'
  | 'prepayments'
  | 'other_current_assets'
  | 'property_plant_equipment'
  | 'investments'
  | 'intangibles'
  | 'goodwill'
  | 'other_non_current_assets'
  | 'accounts_payable'
  | 'short_term_debt'
  | 'tax_payable'
  | 'provisions'
  | 'other_current_liabilities'
  | 'long_term_debt'
  | 'deferred_tax_liabilities'
  | 'other_non_current_liabilities'
  | 'share_capital'
  | 'retained_earnings'
  | 'reserves'
  | 'other_equity'
  | 'cash_holdings_smsf'
  | 'property_values_smsf'
  | 'other_assets_smsf'
  | 'loan_balance_smsf'
  // Aged Debtors/Creditors (legacy, used in BS config)
  | 'aged_debtors_30_plus'
  | 'aged_creditors_30_plus'
  // Sprint 14: Current Assets (6)
  | 'trade_receivables'
  | 'related_party_receivables'
  | 'gst_receivable'
  | 'work_in_progress'
  | 'deposits_paid'
  | 'loans_to_directors'
  // Sprint 14: Non-Current Assets (7)
  | 'land_and_buildings'
  | 'plant_and_machinery'
  | 'motor_vehicles'
  | 'furniture_and_fittings'
  | 'right_of_use_assets'
  | 'investments_in_subsidiaries'
  | 'deferred_tax_assets'
  // Sprint 14: Current Liabilities (7)
  | 'gst_payable'
  | 'payg_withholding'
  | 'superannuation_payable'
  | 'related_party_payables'
  | 'current_portion_long_term_debt'
  | 'lease_liabilities_current'
  | 'employee_entitlements_current'
  // Sprint 14: Non-Current Liabilities (4)
  | 'lease_liabilities_non_current'
  | 'related_party_loans'
  | 'employee_entitlements_non_current'
  | 'loans_from_directors'
  // Sprint 14: Equity (3)
  | 'current_year_profit_loss'
  | 'revaluation_reserve'
  | 'drawings';


// ============================================================================
// Types
// ============================================================================

export type BSSign = '+' | '-';

export interface BSLineItemConfig {
  id: BSLineItemType;
  label: string;
  sign: BSSign;
  isLegacy?: boolean;
}

export interface BSSubcategoryConfig {
  key: string;
  label: string;
  items: BSLineItemConfig[];
}

export interface BSCategoryConfig {
  key: string;
  label: string;
  type: 'input';
  subcategories?: BSSubcategoryConfig[];
  items?: BSLineItemConfig[];
}

export interface BSCalculatedRowConfig {
  key: string;
  label: string;
  type: 'calculated';
  formula: string;
}

export type BSStructureEntry = BSCategoryConfig | BSCalculatedRowConfig;

// ============================================================================
// Current Assets — 4 subcategories
// ============================================================================

const CA_CASH_AND_BANK: BSSubcategoryConfig = {
  key: 'cash_and_bank',
  label: 'Cash & Bank',
  items: [
    { id: 'cash_business_account', label: 'Cash / Business Account', sign: '+' },
  ],
};

const CA_RECEIVABLES: BSSubcategoryConfig = {
  key: 'receivables',
  label: 'Receivables',
  items: [
    { id: 'accounts_receivable', label: 'Accounts Receivable', sign: '+' },
    { id: 'trade_receivables', label: 'Trade Receivables', sign: '+' },
    { id: 'related_party_receivables', label: 'Related Party Receivables', sign: '+' },
    { id: 'gst_receivable', label: 'GST Receivable', sign: '+' },
    { id: 'aged_debtors_30_plus', label: 'Aged Debtors 30+', sign: '+', isLegacy: true },
  ],
};

const CA_INVENTORY_WIP: BSSubcategoryConfig = {
  key: 'inventory_wip',
  label: 'Inventory & WIP',
  items: [
    { id: 'inventory', label: 'Inventory', sign: '+' },
    { id: 'work_in_progress', label: 'Work in Progress', sign: '+' },
  ],
};

const CA_OTHER_CURRENT: BSSubcategoryConfig = {
  key: 'other_current',
  label: 'Other Current Assets',
  items: [
    { id: 'prepayments', label: 'Prepayments', sign: '+' },
    { id: 'deposits_paid', label: 'Deposits Paid', sign: '+' },
    { id: 'loans_to_directors', label: 'Loans to Directors', sign: '+' },
    { id: 'other_current_assets', label: 'Other Current Assets', sign: '+' },
  ],
};

// ============================================================================
// Non-Current Assets — 4 subcategories
// ============================================================================

const NCA_PROPERTY_PPE: BSSubcategoryConfig = {
  key: 'property_ppe',
  label: 'Property, Plant & Equipment',
  items: [
    { id: 'land_and_buildings', label: 'Land & Buildings', sign: '+' },
    { id: 'property_plant_equipment', label: 'Property, Plant & Equipment', sign: '+' },
    { id: 'plant_and_machinery', label: 'Plant & Machinery', sign: '+' },
    { id: 'motor_vehicles', label: 'Motor Vehicles', sign: '+' },
    { id: 'furniture_and_fittings', label: 'Furniture & Fittings', sign: '+' },
  ],
};

const NCA_INTANGIBLE: BSSubcategoryConfig = {
  key: 'intangible',
  label: 'Intangible Assets',
  items: [
    { id: 'intangibles', label: 'Intangibles', sign: '+' },
    { id: 'goodwill', label: 'Goodwill', sign: '+' },
  ],
};

const NCA_INVESTMENTS: BSSubcategoryConfig = {
  key: 'investments_nca',
  label: 'Investments',
  items: [
    { id: 'investments', label: 'Investments', sign: '+' },
    { id: 'investments_in_subsidiaries', label: 'Investments in Subsidiaries', sign: '+' },
  ],
};

const NCA_LEASE_AND_OTHER: BSSubcategoryConfig = {
  key: 'lease_and_other',
  label: 'Lease & Other',
  items: [
    { id: 'right_of_use_assets', label: 'Right-of-Use Assets', sign: '+' },
    { id: 'deferred_tax_assets', label: 'Deferred Tax Assets', sign: '+' },
    { id: 'other_non_current_assets', label: 'Other Non-Current Assets', sign: '+' },
  ],
};

// ============================================================================
// Current Liabilities — 4 subcategories
// ============================================================================

const CL_PAYABLES: BSSubcategoryConfig = {
  key: 'payables',
  label: 'Payables',
  items: [
    { id: 'accounts_payable', label: 'Accounts Payable', sign: '+' },
    { id: 'related_party_payables', label: 'Related Party Payables', sign: '+' },
    { id: 'aged_creditors_30_plus', label: 'Aged Creditors 30+', sign: '+', isLegacy: true },
  ],
};

const CL_TAX_AND_EMPLOYEE: BSSubcategoryConfig = {
  key: 'tax_and_employee',
  label: 'Tax & Employee',
  items: [
    { id: 'tax_payable', label: 'Tax Payable', sign: '+' },
    { id: 'gst_payable', label: 'GST Payable', sign: '+' },
    { id: 'payg_withholding', label: 'PAYG Withholding', sign: '+' },
    { id: 'superannuation_payable', label: 'Superannuation Payable', sign: '+' },
    { id: 'employee_entitlements_current', label: 'Employee Entitlements (Current)', sign: '+' },
  ],
};

const CL_BORROWINGS: BSSubcategoryConfig = {
  key: 'borrowings_cl',
  label: 'Borrowings',
  items: [
    { id: 'short_term_debt', label: 'Short-Term Debt', sign: '+' },
    { id: 'current_portion_long_term_debt', label: 'Current Portion of Long-Term Debt', sign: '+' },
    { id: 'lease_liabilities_current', label: 'Lease Liabilities (Current)', sign: '+' },
  ],
};

const CL_OTHER: BSSubcategoryConfig = {
  key: 'other_cl',
  label: 'Other',
  items: [
    { id: 'provisions', label: 'Provisions', sign: '+' },
    { id: 'other_current_liabilities', label: 'Other Current Liabilities', sign: '+' },
  ],
};

// ============================================================================
// Non-Current Liabilities — flat list (no subcategories)
// ============================================================================

const NCL_ITEMS: BSLineItemConfig[] = [
  { id: 'long_term_debt', label: 'Long-Term Debt', sign: '+' },
  { id: 'lease_liabilities_non_current', label: 'Lease Liabilities (Non-Current)', sign: '+' },
  { id: 'deferred_tax_liabilities', label: 'Deferred Tax Liabilities', sign: '+' },
  { id: 'related_party_loans', label: 'Related Party Loans', sign: '+' },
  { id: 'employee_entitlements_non_current', label: 'Employee Entitlements (Non-Current)', sign: '+' },
  { id: 'loans_from_directors', label: 'Loans from Directors', sign: '+' },
  { id: 'other_non_current_liabilities', label: 'Other Non-Current Liabilities', sign: '+' },
];

// ============================================================================
// Equity — flat list
// ============================================================================

const EQUITY_ITEMS: BSLineItemConfig[] = [
  { id: 'share_capital', label: 'Share Capital', sign: '+' },
  { id: 'retained_earnings', label: 'Retained Earnings', sign: '+' },
  { id: 'current_year_profit_loss', label: 'Current Year Profit / Loss', sign: '+' },
  { id: 'reserves', label: 'Reserves', sign: '+' },
  { id: 'revaluation_reserve', label: 'Revaluation Reserve', sign: '+' },
  { id: 'drawings', label: 'Drawings', sign: '-' },
  { id: 'other_equity', label: 'Other Equity', sign: '+' },
];

// ============================================================================
// BS_STRUCTURE — Full Hierarchy
//
// Order matches standard Balance Sheet presentation:
//   Current Assets → [Total CA] → Non-Current Assets → [Total NCA] →
//   [Total Assets] → Current Liabilities → [Total CL] →
//   Non-Current Liabilities → [Total NCL] → [Total Liabilities] →
//   Equity → [Total Equity] → [Balance Check]
// ============================================================================

export const BS_STRUCTURE: BSStructureEntry[] = [
  // --- Input: Current Assets ---
  {
    key: 'current_assets',
    label: 'Current Assets',
    type: 'input' as const,
    subcategories: [CA_CASH_AND_BANK, CA_RECEIVABLES, CA_INVENTORY_WIP, CA_OTHER_CURRENT],
  },
  // --- Calculated: Total Current Assets ---
  {
    key: 'total_current_assets',
    label: 'Total Current Assets',
    type: 'calculated' as const,
    formula: 'Sum of all Current Asset items',
  },
  // --- Input: Non-Current Assets ---
  {
    key: 'non_current_assets',
    label: 'Non-Current Assets',
    type: 'input' as const,
    subcategories: [NCA_PROPERTY_PPE, NCA_INTANGIBLE, NCA_INVESTMENTS, NCA_LEASE_AND_OTHER],
  },
  // --- Calculated: Total Non-Current Assets ---
  {
    key: 'total_non_current_assets',
    label: 'Total Non-Current Assets',
    type: 'calculated' as const,
    formula: 'Sum of all Non-Current Asset items',
  },
  // --- Calculated: Total Assets ---
  {
    key: 'total_assets',
    label: 'Total Assets',
    type: 'calculated' as const,
    formula: 'Total Current Assets + Total Non-Current Assets',
  },
  // --- Input: Current Liabilities ---
  {
    key: 'current_liabilities',
    label: 'Current Liabilities',
    type: 'input' as const,
    subcategories: [CL_PAYABLES, CL_TAX_AND_EMPLOYEE, CL_BORROWINGS, CL_OTHER],
  },
  // --- Calculated: Total Current Liabilities ---
  {
    key: 'total_current_liabilities',
    label: 'Total Current Liabilities',
    type: 'calculated' as const,
    formula: 'Sum of all Current Liability items',
  },
  // --- Input: Non-Current Liabilities ---
  {
    key: 'non_current_liabilities',
    label: 'Non-Current Liabilities',
    type: 'input' as const,
    items: NCL_ITEMS,
  },
  // --- Calculated: Total Non-Current Liabilities ---
  {
    key: 'total_non_current_liabilities',
    label: 'Total Non-Current Liabilities',
    type: 'calculated' as const,
    formula: 'Sum of all Non-Current Liability items',
  },
  // --- Calculated: Total Liabilities ---
  {
    key: 'total_liabilities',
    label: 'Total Liabilities',
    type: 'calculated' as const,
    formula: 'Total Current Liabilities + Total Non-Current Liabilities',
  },
  // --- Input: Equity ---
  {
    key: 'equity',
    label: 'Equity',
    type: 'input' as const,
    items: EQUITY_ITEMS,
  },
  // --- Calculated: Total Equity ---
  {
    key: 'total_equity',
    label: 'Total Equity',
    type: 'calculated' as const,
    formula: 'Sum of all Equity items',
  },
  // --- Calculated: Balance Check ---
  {
    key: 'balance_check',
    label: 'Balance Check',
    type: 'calculated' as const,
    formula: 'Total Assets − Total Liabilities − Total Equity',
  },
];

// ============================================================================
// Reverse Lookup Maps
// ============================================================================

const _categoryLookup: Partial<Record<BSLineItemType, string>> = {};
const _subcategoryLookup: Partial<Record<BSLineItemType, string>> = {};

for (const entry of BS_STRUCTURE) {
  if (entry.type !== 'input') continue;
  const inputEntry = entry as BSCategoryConfig;

  if (inputEntry.subcategories) {
    for (const sub of inputEntry.subcategories) {
      for (const item of sub.items) {
        _categoryLookup[item.id] = inputEntry.key;
        _subcategoryLookup[item.id] = sub.key;
      }
    }
  }

  if (inputEntry.items) {
    for (const item of inputEntry.items) {
      _categoryLookup[item.id] = inputEntry.key;
    }
  }
}

// ============================================================================
// Exports: Helper Functions
// ============================================================================

/**
 * Returns the top-level category key for a given BS line item id.
 * Returns undefined if the item is not found in any category.
 *
 * @example
 *   getCategoryForBSLineItem('trade_receivables')  // 'current_assets'
 *   getCategoryForBSLineItem('long_term_debt')  // 'non_current_liabilities'
 *   getCategoryForBSLineItem('drawings')  // 'equity'
 */
export function getCategoryForBSLineItem(lineItemId: BSLineItemType): string | undefined {
  return _categoryLookup[lineItemId];
}

/**
 * Returns the subcategory key for a given BS line item id.
 * Returns undefined for items in flat categories (NCL, Equity).
 *
 * @example
 *   getSubcategoryForBSLineItem('trade_receivables')  // 'receivables'
 *   getSubcategoryForBSLineItem('long_term_debt')  // undefined
 */
export function getSubcategoryForBSLineItem(lineItemId: BSLineItemType): string | undefined {
  return _subcategoryLookup[lineItemId];
}

/**
 * Returns the 8 calculated row definitions for the Simplified view.
 */
export function getSimplifiedBSRows(): BSCalculatedRowConfig[] {
  return BS_STRUCTURE.filter(
    (entry): entry is BSCalculatedRowConfig => entry.type === 'calculated'
  );
}

/**
 * Returns all editable (input) line items across all categories, flattened.
 */
export function getAllBSInputRows(): BSLineItemConfig[] {
  const rows: BSLineItemConfig[] = [];

  for (const entry of BS_STRUCTURE) {
    if (entry.type !== 'input') continue;
    const inputEntry = entry as BSCategoryConfig;

    if (inputEntry.subcategories) {
      for (const sub of inputEntry.subcategories) {
        rows.push(...sub.items);
      }
    }

    if (inputEntry.items) {
      rows.push(...inputEntry.items);
    }
  }

  return rows;
}
